import api from './api';
import type { PatientPlanRecord } from '../types/patientPlan.types';
import { buildCompositePatientId, parseCompositePatientId } from '../utils/patientUtils';

const API_PREFIX = '/api';

// Enable detailed logging (can be toggled via browser console or env var)
const DEBUG_PATIENT_SERVICE = import.meta.env.VITE_DEBUG_PATIENT_SERVICE === 'true';

/**
 * Per-table tally from POST /api/patients/merge-profiles. `billingItems` IS the
 * subscription count — a PHOX subscription/refill is a billing_items row.
 */
export interface PatientMergeCounts {
  orders: number;
  billingItems: number;
  troubleshootingLogs: number;
  patientNotes: number;
  clinicalNotes: number;
  patientDocuments: number;
  total: number;
}

/** Who is on one side of the merge. `exists: false` = no PHOX patients row. */
export interface PatientMergeProfileSummary {
  compositeId: string;
  exists: boolean;
  name?: string;
  dateOfBirth?: string;
  patientNumber?: string;
}

export interface PatientMergeResult {
  success: boolean;
  dryRun: boolean;
  sourcePatientId: string;
  targetPatientId: string;
  sourcePatient: PatientMergeProfileSummary;
  targetPatient: PatientMergeProfileSummary;
  reassigned: PatientMergeCounts;
  /** Dry runs only — pass back as confirmToken to perform the live apply. */
  confirmToken?: string;
  message?: string;
}

export interface PatientAllergy {
  id: number | string;
  patientId: number;
  codeType?: number;
  code?: string;
  description: string;
  severity?: number;
  reaction?: string;
  source?: string;
  dateAdded?: string;
  isActive?: boolean;
}

export interface PatientAllergyStatus {
  /** True when Kroll confirms "No Known Allergies" — either via a Pat flag OR a sentinel PatAlg row. */
  noKnownAllergies: boolean;
  /**
   * How the NKA state was resolved:
   * - `kroll-flag`   — boolean column on Pat
   * - `sentinel-row` — a PatAlg row whose description is "No Known Allergies" / "NKA" / "NKDA"
   * - `unknown`      — no NKA flag and no sentinel row; the patient has no allergy info recorded
   */
  noKnownAllergiesSource: 'kroll-flag' | 'sentinel-row' | 'unknown';
  /** Name of the Pat column the NKA flag was read from (for debugging schema drift). */
  noKnownAllergiesColumn?: string;
  /** Documented allergies from Kroll PatAlg, with NKA sentinel rows stripped out. */
  allergies: PatientAllergy[];
}

/**
 * PHOX-owned medical profile — allergies and medical conditions maintained
 * natively in PHOX (system of record), distinct from the read-only Kroll
 * allergy pull (see PatientAllergyStatus).
 */
export interface PatientMedicalProfile {
  allergies: string[];
  medicalConditions: string[];
  /** Best possible medication history (BPMH) — every med the patient is on. */
  medications: string[];
  /** Free-text weight incl. unit (e.g. "72 kg"), or null when not recorded. */
  weight: string | null;
  updatedAt: string | null;
}

export interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  healthCardNumber: string;
  patientId: string;
  mrn: string;
  primaryDiagnosis?: string;
  secondaryDiagnosis?: string;
  email?: string;
  phone?: string;
  primaryPhone?: string;
  mobilePhone?: string;
  addresses?: PatientAddress[];
  insurance?: PatientInsurance[];
  medications?: PatientMedication[];
  careTeam?: PatientCareTeam[];
  adherenceRate?: number;
  monthlyCopay?: number;
  daysUntilRefill?: number;
  openTasks?: number;
  totalDeliveries?: number;
  patientSince?: string;
  /**
   * Kroll nursing-home (NH) group membership, resolved live by the Kroll patient
   * queries (`NH_MEMBERSHIP_SELECT` in `kroll-patient.service.ts`). Present only
   * when the patient is an ACTIVE member of an ACTIVE group; `undefined` means
   * "not a member" — never a placeholder home. Shape matches `KrollGroupInfo`
   * so it can be handed straight to `GroupPatientChip`.
   */
  krollGroup?: { groupId?: number; groupName: string } | null;
  /**
   * Kroll `Pat.Active`. `false` = the patient's Kroll file is closed. `undefined`
   * means the query never selected it — NOT "active". (GP-1402 / audit K-22.)
   */
  active?: boolean;
  /**
   * Kroll `Pat.AlternateLastName` — the maiden / former surname the patient is
   * also filed under. Patient search matches on it, so a row can surface under a
   * name that isn't `lastName`; showing it is how staff see WHY it matched.
   */
  alternateLastName?: string;
  /** Kroll `Pat.NHInactive` — the patient's group-home MEMBERSHIP has ended. Not a patient status. */
  nhInactive?: boolean;
  /**
   * Kroll `Pat.NHDeceasedDate` (YYYY-MM-DD). `null` = Kroll was asked and has no
   * date of death; `undefined` = the query didn't ask.
   */
  deceasedDate?: string | null;
  /**
   * PRX-36 — the PHOX-owned status (`patients.status`), NOT the Kroll `active`
   * flag above: this one is manual-only and never synced from a PMS (R1), and
   * it is what actually blocks a new order or subscription. `undefined` means
   * the search path didn't return it, not "active".
   */
  status?: 'active' | 'inactive' | 'deceased' | 'transferred';
}

export interface PatientAddress {
  id: string;
  type: 'PRIMARY' | 'SHIPPING' | 'BILLING' | 'TEMPORARY';
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zipCode: string;
  isActive: boolean;
  isPreferred: boolean;
}

// V2 Patient Address Response (from V2 API)
export interface PatientAddressV2 {
  id: string;
  patientId: string;
  pharmacyId: string;
  addressType: 'home' | 'work' | 'temporary' | 'billing' | 'shipping';
  label?: string;
  isDefaultDelivery: boolean;
  isPrimary: boolean;
  isActive: boolean;
  streetAddress: string;
  unitNumber?: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  fullAddress: string;
  formattedAddress?: string;
  deliveryInstructions?: string;
  accessCode?: string;
  contactName?: string;
  contactPhone?: string;
  latitude?: number;
  longitude?: number;
  googlePlaceId?: string;
  validationStatus: 'validated' | 'unvalidated' | 'partial' | 'pending' | 'failed';
  validationScore?: number;
  isOurAuthority?: boolean;
  requiresValidation?: boolean;
  validationConfirmedAt?: Date | string;
  syncStatus: 'synced' | 'pending' | 'failed' | 'never';
  sourceSystem: 'manual' | 'kroll' | 'import';
  lastSyncedAt?: Date | string;
  lastUsedAt?: Date | string;
  useCount: number;
  createdAt: Date | string;
  updatedAt: Date | string;
  internalNotes?: string;
}

export interface PatientInsurance {
  id: string;
  type: 'PRIMARY' | 'SECONDARY' | 'TERTIARY';
  insuranceCompany: string;
  policyNumber: string;
  groupNumber?: string;
  bin?: string;
  pcn?: string;
  isActive: boolean;
  deductible?: number;
  deductibleMet?: number;
  outOfPocketMax?: number;
  outOfPocketMet?: number;
}

export interface PatientMedication {
  id: string;
  medicationName: string;
  strength: string;
  dosageForm: string;
  sig: string;
  startDate: string;
  nextFillDate?: string;
  adherenceRate?: number;
  priorAuthStatus?: string;
  priorAuthExpiry?: string;
  copayCardActive?: boolean;
  monthlyFillHistory?: { month: string; status: 'active' | 'scheduled' | 'missed' }[];
  metadata?: any; // For storing additional system-specific data (e.g., Kroll fields)
}

export interface PatientCareTeam {
  id: string;
  role: string;
  name: string;
  phone?: string;
  email?: string;
}

export interface PatientActivity {
  id: string;
  type: 'billing' | 'delivery' | 'clinical' | 'communication';
  title: string;
  description: string;
  timestamp: string;
  performedBy?: string;
  metadata?: any;
}

export interface PatientTask {
  id: string;
  priority: 'high' | 'medium' | 'low';
  type: string;
  title: string;
  description: string;
  dueDate?: string;
  status: 'open' | 'in_progress' | 'completed';
}

export interface PatientNote {
  id: string;
  date: string;
  author: string;
  type: 'clinical' | 'general' | 'billing';
  content: string;
}

export interface DeliveryHistory {
  id: string;
  date: string;
  medication: string;
  quantity: string;
  address: string;
  status: 'delivered' | 'attempted' | 'returned' | 'scheduled';
  signedBy?: string;
}

class PatientService {
  async getPatient(pharmacyId: string, patientId: string): Promise<Patient> {
    const response = await api.get(`${API_PREFIX}/patients/mqtt/${pharmacyId}/${patientId}`);
    return response.data;
  }

  async getPatientActivities(patientId: string): Promise<PatientActivity[]> {
    const response = await api.get(`${API_PREFIX}/patients/${patientId}/activities`);
    return response.data;
  }

  async getPatientTasks(patientId: string): Promise<PatientTask[]> {
    const response = await api.get(`${API_PREFIX}/patients/${patientId}/tasks`);
    return response.data;
  }

  async getPatientNotes(patientId: string, pharmacyId: string): Promise<PatientNote[]> {
    const compositeId = buildCompositePatientId(patientId, pharmacyId);
    const response = await api.get(`${API_PREFIX}/patients/${compositeId}/all-notes`);
    return response.data;
  }

  async getDeliveryHistory(patientId: string, pharmacyId: string): Promise<DeliveryHistory[]> {
    // TODO: Delivery history endpoint not yet implemented in backend
    // This will return 404 until the backend endpoint is created
    const compositeId = buildCompositePatientId(patientId, pharmacyId);
    const response = await api.get(`${API_PREFIX}/patients/${compositeId}/deliveries`);
    return response.data;
  }

  async updatePatientAddress(patientId: string, addressId: string, address: Partial<PatientAddress>): Promise<PatientAddress> {
    const response = await api.put(
      `${API_PREFIX}/patients/${patientId}/addresses/${addressId}`,
      address
    );
    return response.data;
  }

  /**
   * Get all addresses for a patient using the V2 REST endpoint
   * @param patientId - Patient ID (UUID, composite ID, or patient number)
   * @param options - Optional filters
   * @returns Promise<PatientAddressV2[]>
   */
  async getPatientAddresses(
    patientId: string,
    options?: {
      activeOnly?: boolean;
      addressType?: 'home' | 'work' | 'temporary' | 'billing' | 'shipping';
    }
  ): Promise<PatientAddressV2[]> {
    const params: Record<string, any> = {};
    if (options?.activeOnly !== undefined) params.activeOnly = options.activeOnly;
    if (options?.addressType) params.addressType = options.addressType;

    // Use the correct patient-addresses endpoint (not v2/patients)
    const response = await api.get(`${API_PREFIX}/patient-addresses/${patientId}/list`, { params });

    // Response may be array directly or wrapped in {success, data} structure
    if (response.data && typeof response.data === 'object') {
      // If wrapped in {success, data}, extract the data array
      if ('data' in response.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      // If it's already an array, return as-is
      if (Array.isArray(response.data)) {
        return response.data;
      }
    }

    return response.data;
  }

  async verifyInsurance(patientId: string, insuranceId: string): Promise<any> {
    const response = await api.post(
      `${API_PREFIX}/patients/${patientId}/insurance/${insuranceId}/verify`,
      {}
    );
    return response.data;
  }

  async searchPatientsWithMQTT(searchParams: {
    lastName?: string;
    firstName?: string;
    searchTerm?: string; // Keep for backward compatibility
    searchType?: 'name' | 'dob' | 'healthCard' | 'phone' | 'all';
    limit?: number;
    pharmacyId?: string; // Add pharmacy filter
  }, signal?: AbortSignal): Promise<{
    searchTerm: string;
    totalResults: number;
    pharmacyResults: Array<{
      pharmacyId: string;
      pharmacyName: string;
      patients: Patient[];
      total: number;
      searchTime: number;
    }>;
    timestamp: Date;
  }> {
    const response = await api.post(`${API_PREFIX}/patients/mqtt/search`, searchParams, { signal });
    return response.data;
  }

  async getMQTTConnectionStats(): Promise<any> {
    const response = await api.get(`${API_PREFIX}/patients/mqtt/connection-stats`);
    return response.data;
  }

  async getPatientFromMQTT(pharmacyId: string, patientId: string): Promise<Patient> {
    const response = await api.get(`${API_PREFIX}/patients/mqtt/${pharmacyId}/${patientId}`);
    return response.data;
  }

  async getPatientAllergiesFromKroll(
    pharmacyId: string,
    patientId: string,
  ): Promise<PatientAllergyStatus> {
    const response = await api.get(
      `${API_PREFIX}/patients/mqtt/${pharmacyId}/${patientId}/allergies`,
    );
    return response.data;
  }

  /**
   * Read the PHOX-owned medical profile (allergies + medical conditions) for a
   * patient. Empty arrays mean nothing has been recorded in PHOX yet.
   */
  async getMedicalProfile(compositePatientId: string): Promise<PatientMedicalProfile> {
    const response = await api.get(
      `${API_PREFIX}/patients/${compositePatientId}/medical-profile`,
    );
    return response.data;
  }

  /**
   * Update the PHOX-owned medical profile. Omitted fields are left unchanged;
   * an empty array clears that field.
   */
  async updateMedicalProfile(
    compositePatientId: string,
    updates: {
      allergies?: string[];
      medicalConditions?: string[];
      medications?: string[];
      weight?: string | null;
    },
  ): Promise<PatientMedicalProfile & { success: boolean }> {
    const response = await api.put(
      `${API_PREFIX}/patients/${compositePatientId}/medical-profile`,
      updates,
    );
    return response.data;
  }

  async syncAndCreatePatientFromMQTT(pharmacyId: string, krollPatientId: string): Promise<{
    success: boolean;
    patient: Patient & { 
      localId: string; 
      compositeId: string; 
      addresses: any[];
    };
    message: string;
  }> {
    if (DEBUG_PATIENT_SERVICE) {
      console.log(`[PatientService] Syncing and creating patient ${krollPatientId} from pharmacy ${pharmacyId}`);
    }
    const response = await api.post(`${API_PREFIX}/patients/mqtt/${pharmacyId}/${krollPatientId}/sync-and-create`);
    return response.data;
  }

  async getPatientMedicationsFromKroll(pharmacyId: string, patientId: string): Promise<{
    patientId: string;
    pharmacyId: string;
    pharmacyName: string;
    medications: Array<{
      rxNumber: string;
      originalRxNumber: string;
      status: number;
      drugName: string;
      genericName: string;
      brandName: string;
      din: string;
      strength: string;
      sig: string;
      quantity: number;
      daysSupply: number;
      refillsRemaining: number;
      refillsAuthorized: number;
      fillDate: string;
      firstFillDate: string;
      lastFillDate: string;
      nextFillDate: string;
      expiryDate: string;
      effectiveExpiryDate: string;
      stopDate: string;
      writtenDate: string;
      prescriber: {
        name: string;
        id: string;
      };
      lot: string;
      cost: number;
      fee: number;
      markup: number;
      price: number;
      schedule: string;
      autoRefill: boolean;
      isSuspended: boolean;
      isHidden: boolean;
      isMistake: boolean;
      dueDays: number;
      workflowQueue: string;
    }>;
    total: number;
    timestamp: Date;
  }> {
    // Updated to use test-rx-query endpoint with LU Code support (Phase 1)
    const response = await api.get(`${API_PREFIX}/todo/test-rx-query/${pharmacyId}/${patientId}`, {
      params: { filter: 'active' }
    });

    // Transform response to match expected format
    if (response.data?.data?.results) {
      return {
        patientId,
        pharmacyId,
        pharmacyName: response.data.data.pharmacy?.systemType || 'kroll',
        medications: response.data.data.results,
        total: response.data.data.count || response.data.data.results.length,
        timestamp: new Date()
      };
    }

    return response.data;
  }

  /**
   * Get RAW patient prescriptions with no column name transformation
   * Returns exact SQL Server column names (PatID, DispQty, RemQty, etc.)
   * @param pharmacyId - Pharmacy ID
   * @param patientId - Patient ID or composite ID
   * @param filter - 'active' | 'inactive' | 'all' | 'unfilled' (default: 'active')
   * @param limit - Maximum number of results (default: 50)
   */
  async getRawPatientPrescriptions(
    pharmacyId: string,
    patientId: string,
    filter: 'active' | 'inactive' | 'all' | 'unfilled' = 'active',
    limit: number = 50
  ): Promise<{
    success: boolean;
    data: {
      results: Array<any>; // RAW results with SQL Server column names
      count: number;
      pharmacy: {
        id: string;
        systemType: string;
      };
      queryInfo: {
        filter: string;
        limit: number;
        note: string;
      };
    };
    message: string;
  }> {
    if (DEBUG_PATIENT_SERVICE) {
      console.log('[PatientService] getRawPatientPrescriptions - Calling RAW endpoint');
      console.log('[PatientService] Pharmacy:', pharmacyId, 'Patient:', patientId, 'Filter:', filter);
    }

    const response = await api.get(
      `${API_PREFIX}/todo/raw-rx-query/${pharmacyId}/${patientId}`,
      {
        params: { filter, limit }
      }
    );

    if (DEBUG_PATIENT_SERVICE) {
      console.log('[PatientService] RAW prescriptions query returned', response.data.data.count, 'results');
      console.log('[PatientService] Query info:', response.data.data.queryInfo);
    }

    return response.data;
  }

  // New method using the improved Rx query with better performance and field mappings
  async getPatientMedicationsImproved(
    pharmacyId: string,
    patientId: string,
    filter: 'active' | 'inactive' | 'all' = 'active'
  ): Promise<{
    success: boolean;
    data: {
      query: string;
      results: Array<{
        RxNum: string;
        RxStatus: number;
        RxType: number;
        PatientId: string;
        PatientName: string;
        DocId: string;
        DoctorName: string;
        DIN: string;
        BrandName: string;
        GenericName: string;
        DispenseQuantity: number;
        Sig: string;
        DaysSupply: number;
        RefillsRemaining: number;
        OriginalDate: string;
        ExpiryDate: string;
        LastFillDate: string;
        NextFillDate: string;
        RxBarcodeId: string;
        Strength: string;
        Cost: number;
        Fee: number;
        Markup: number;
        RetailPrice: number;
        AcqCost: number;
        PrescriberId: string;
        PrescriberName: string;
        PrescriberPhone: string;
        PrescriberFax: string;
      }>;
      count: number;
      pharmacy: {
        id: string;
        systemType: string;
      };
    };
  }> {
    // Extract patient number from composite ID if needed
    const patientNumber = parseCompositePatientId(patientId).patientNumber || patientId;
    if (DEBUG_PATIENT_SERVICE) {
      console.log('Calling API with filter:', filter, 'URL:', `${API_PREFIX}/billing-queue/get-recent-rx-query/${pharmacyId}/${patientNumber}`);
    }
    const response = await api.get(`${API_PREFIX}/billing-queue/get-recent-rx-query/${pharmacyId}/${patientNumber}`, {
      params: { filter }
    });
    if (DEBUG_PATIENT_SERVICE) {
      console.log('API Response for filter', filter, ':', response.data);
    }
    return response.data;
  }

  /**
   * OPTIMIZED query for order/subscription creation
   * Uses simplified query with only 22 essential fields (vs 33 in get-recent-rx-query)
   * Auto-filters out mistakes (IsMistake = 0)
   * Parameterized for better reusability and performance
   * Accepts composite patient ID - backend handles extraction for MQTT
   */
  async getPatientPrescriptions(
    pharmacyId: string,
    patientId: string,
    options?: {
      filter?: 'active' | 'inactive' | 'all';
      limit?: number;
      includeInactive?: boolean;
    }
  ): Promise<{
    success: boolean;
    data: {
      results: Array<{
        // Core Rx Info (3 fields)
        RxNum: string;
        RxStatus: number;
        Inactive: number;
        // Patient Info (2 fields)
        PatientId: string;
        PatientName: string;
        // Drug Info (6 fields)
        BrandName: string;
        GenericName: string;
        Strength: string;
        DIN: string;
        Sig: string;
        DispenseQuantity: number;
        // Refill/Date Info (4 fields)
        DaysSupply: number;
        RefillsRemaining: number;
        LastFillDate: string;
        NextFillDate: string;
        // Prescriber Info (2 fields)
        DocId: string;
        DoctorName: string;
        // Pricing Info - includes GP% calculation fields
        Cost: number;
        Fee: number;
        Markup: number;
        RetailPrice: number;
        CustomerPrice: number;
        InsurancePays: number;  // For GP%: ((InsurancePays + CustomerPrice) - AcqCost) / (InsurancePays + CustomerPrice)
        AcqCost: number;  // Acquisition cost
      }>;
      count: number;
      pharmacy: {
        id: string;
        systemType: string;
      };
      queryInfo: {
        filter: string;
        limit: number;
        includeInactive: boolean;
        fieldsReturned: number;
        queryLength: number;
      };
    };
    message: string;
  }> {
    // Send composite ID to backend - extraction handled there
    const params: Record<string, any> = {};
    if (options?.filter) params.filter = options.filter;
    if (options?.limit) params.limit = options.limit;
    if (options?.includeInactive !== undefined) params.includeInactive = options.includeInactive;

    if (DEBUG_PATIENT_SERVICE) {
      console.log('[PatientService] getPatientPrescriptions - Calling optimized endpoint');
      console.log('[PatientService] Pharmacy:', pharmacyId, 'Patient:', patientId);
      console.log('[PatientService] Params:', params);
    }

    const response = await api.get(
      `${API_PREFIX}/todo/patient-prescriptions/${pharmacyId}/${patientId}`,
      { params }
    );

    if (DEBUG_PATIENT_SERVICE) {
      console.log('[PatientService] Patient prescriptions query returned', response.data.data.count, 'results');
      console.log('[PatientService] Query info:', response.data.data.queryInfo);
    }

    return response.data;
  }

  async getPatientCommentsFromKroll(pharmacyId: string, patientId: string): Promise<{
    patientId: string;
    pharmacyId: string;
    pharmacyName: string;
    comments: Array<{
      id: string;
      patientId: string;
      comment: string;
      date: string;
      createdBy: string;
      type: string;
    }>;
    total: number;
    timestamp: Date;
  }> {
    const response = await api.get(`${API_PREFIX}/patients/mqtt/${pharmacyId}/${patientId}/comments`);
    return response.data;
  }

  async getPatientPhonesFromKroll(pharmacyId: string, patientId: string): Promise<{
    patientId: string;
    pharmacyId: string;
    pharmacyName: string;
    phones: Array<{
      id: string;
      phone: string;
      type: string;
      isPrimary: boolean;
      note: string;
      sequence: number;
    }>;
    total: number;
    timestamp: Date;
  }> {
    const response = await api.get(`${API_PREFIX}/patients/mqtt/${pharmacyId}/${patientId}/phones`);
    return response.data;
  }

  /**
   * Patient insurance / benefit plans from the PMS (Kroll or Fillware).
   * PRX-33: the endpoint answers a BARE ARRAY (`PatientPlanDto[]`) — this
   * signature used to promise a `{ plans: [...] }` wrapper that never existed,
   * and the one consumer read `.plans` off the array, so the Insurance tab was
   * empty for every patient.
   */
  async getPatientPlansFromKroll(pharmacyId: string, patientId: string): Promise<PatientPlanRecord[]> {
    const response = await api.get(`${API_PREFIX}/patients/mqtt/${pharmacyId}/${patientId}/plans`);
    return response.data;
  }

  // Search patients by phone number via Kroll MQTT
  async searchPatientsByPhone(pharmacyId: string, phoneNumber: string): Promise<Patient[]> {
    const digits = phoneNumber.replace(/\D/g, '');
    // Use last 10 digits (strip country code) for Kroll matching
    const searchDigits = digits.length > 10 ? digits.slice(-10) : digits;

    if (searchDigits.length < 7) return [];

    const results = await this.searchPatientsWithMQTT({
      searchTerm: searchDigits,
      searchType: 'phone',
      pharmacyId,
      limit: 20,
    });

    return results.pharmacyResults.flatMap(r => r.patients);
  }

  // Convenience method for searching patients
  async searchPatients(pharmacyId: string, searchTerm: string): Promise<Patient[]> {
    // Parse "Last, First" format for more precise search
    const parsedSearch = this.parseSearchTerm(searchTerm);

    const results = await this.searchPatientsWithMQTT({
      ...parsedSearch,
      pharmacyId,
      searchType: 'name',
      limit: 20
    });
    // Extract patients from the pharmacy results
    return results.pharmacyResults.flatMap(r => r.patients);
  }

  // Helper to parse "Last, First" or single name
  private parseSearchTerm(searchTerm: string): { lastName?: string; firstName?: string; searchTerm?: string } {
    const trimmed = searchTerm.trim();

    // Check if it contains a comma (Last, First format)
    if (trimmed.includes(',')) {
      const parts = trimmed.split(',').map(p => p.trim());
      return {
        lastName: parts[0],
        firstName: parts[1] || undefined
      };
    }

    // Check if it contains a space (could be "First Last" or "Last First")
    if (trimmed.includes(' ')) {
      const parts = trimmed.split(/\s+/).filter(p => p.length > 0);
      // Assume last part is last name, rest is first name
      return {
        lastName: parts[parts.length - 1],
        firstName: parts.slice(0, -1).join(' ') || undefined
      };
    }

    // Single word - treat as last name search
    return {
      lastName: trimmed
    };
  }

  /**
   * Update delivery preferences for a patient
   * @param patientId - Patient ID (composite ID or patient number)
   * @param preferences - Delivery preferences to update
   * @returns Promise<any>
   */
  async updateDeliveryPreferences(
    patientId: string,
    preferences: {
      cycleLength?: number;
      preferredDay?: 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI';
      courierPreference?: 'PUROLATOR' | 'FEDEX' | 'CITY_EXPRESS' | 'INTERNAL';
      requireSignature?: boolean;
      temperatureControlled?: boolean;
      eveningDelivery?: boolean;
      pickUpByDefault?: boolean;
      /** GP-1396: specialty pharmacy that dispenses for this patient (null clears it). */
      dispensingPharmacy?: string | null;
      deliveryInstructions?: string;
    }
  ): Promise<any> {
    const response = await api.put(
      `${API_PREFIX}/patients/${patientId}/delivery-preferences`,
      preferences
    );
    return response.data;
  }

  /**
   * GP-1396: the selectable dispensing pharmacies.
   *
   * Fetched rather than hardcoded so the Patient 360 selector, the IV queue
   * filter, and the backend's own validation all read ONE list
   * (`backend-v2/src/patient/constants/dispensing-pharmacies.ts`).
   */
  async getDispensingPharmacies(): Promise<string[]> {
    const response = await api.get(`${API_PREFIX}/patients/dispensing-pharmacies`);
    return response.data?.data ?? [];
  }

  /**
   * Get delivery preferences for a patient
   * @param patientId - Patient ID (composite ID or patient number)
   * @returns Promise<any>
   */
  async getDeliveryPreferences(patientId: string): Promise<any> {
    const response = await api.get(`${API_PREFIX}/patients/${patientId}/delivery-preferences`);
    return response.data;
  }

  /**
   * T087: Search patients in local database by phone, address, or name
   * @param searchParams - Search criteria
   * @returns Promise with search results
   */
  async searchPatientsLocal(searchParams: {
    phone?: string;
    street?: string;
    city?: string;
    postalCode?: string;
    name?: string;
    pharmacyId?: string;
  }): Promise<{
    success: boolean;
    patients: Array<{
      id: string;
      compositePatientId: string;
      externalId: string;
      pharmacyId: string;
      firstName: string;
      lastName: string;
      dateOfBirth?: string;
      phone?: string;
      email?: string;
      addressStreet?: string;
      addressCity?: string;
      addressProvince?: string;
      addressPostalCode?: string;
      preferredContactMethod?: string;
    }>;
    total: number;
  }> {
    const params = new URLSearchParams();
    if (searchParams.phone) params.set('phone', searchParams.phone);
    if (searchParams.street) params.set('street', searchParams.street);
    if (searchParams.city) params.set('city', searchParams.city);
    if (searchParams.postalCode) params.set('postalCode', searchParams.postalCode);
    if (searchParams.name) params.set('name', searchParams.name);
    if (searchParams.pharmacyId) params.set('pharmacyId', searchParams.pharmacyId);

    const response = await api.get(`${API_PREFIX}/patients/search?${params.toString()}`);
    return response.data;
  }

  /**
   * T084: Update patient contact preference
   * @param compositePatientId - Patient ID in format pharmacyId_patientId
   * @param preferredContactMethod - SMS, EMAIL, or PHONE
   * @returns Promise with updated patient
   */
  async updateContactPreference(
    compositePatientId: string,
    preferredContactMethod: 'SMS' | 'EMAIL' | 'PHONE'
  ): Promise<{
    success: boolean;
    patient: {
      id: string;
      compositePatientId: string;
      preferredContactMethod: string;
    };
  }> {
    const response = await api.patch(
      `${API_PREFIX}/patients/${compositePatientId}/contact-preference`,
      { preferredContactMethod }
    );
    return response.data;
  }

  /**
   * GP-973 — admin patient merge. Consolidates the PHOX data left behind on a
   * stale duplicate profile (source) onto the surviving one (target) after the two
   * Kroll profiles were merged on the Kroll side.
   *
   * `dryRun: true` is the PREVIEW — it writes nothing and returns both profiles'
   * identity plus the per-table counts it WOULD move, along with the `confirmToken`
   * that a live apply requires. Sending `dryRun: false` without that token is
   * rejected by the backend, so the preview step cannot be skipped.
   */
  async mergeProfiles(params: {
    sourcePatientId: string;
    targetPatientId: string;
    dryRun: boolean;
    confirmToken?: string;
  }): Promise<PatientMergeResult> {
    const response = await api.post(`${API_PREFIX}/patients/merge-profiles`, params);
    return response.data;
  }
}

const patientService = new PatientService();
export default patientService;